import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactElement,
  type JSX,
} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  desktopAndUpLinaria,
  mediaDesktopAndUp,
} from '../../../constants/customMedia';
import {Button} from '../../atoms/Buttons/Button';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {Image as ImageType} from '../../molecules/ProductTileImage/ProductTileImage';

interface OutfitCollageProps {
  ['data-testid']?: string;
  className?: string;
  ctaLabel: string;
  onClick?: VoidFunction;
  primaryImage: ImageType;
  PrimaryImageContent?: React.ReactNode;
  secondaryImages: {
    element: Omit<ImageType, 'padded'> | ReactElement;
  }[];
  primaryImageLinkTarget?: LinkTarget;
  onPrimaryCtaClick?: VoidFunction;
  PrimaryTracker?: FC<PropsWithChildren>;
  renderWrapperAs?: keyof JSX.IntrinsicElements;
  ctaVariant?: ComponentProps<typeof Button>['variant'];
}

interface LinkTarget {
  url: string;
  external: boolean;
}
const SecondaryProductElement = ({
  secondaryImages,
}: {
  secondaryImages: {
    element: Omit<ImageType, 'padded'> | ReactElement;
  }[];
}) => {
  return (
    <>
      {secondaryImages.slice(0, 3).map((image, index) => {
        return (
          <ProductImageWrapper key={index}>
            {'src' in image.element! ? (
              <ProductImage padded {...image.element} />
            ) : (
              image.element
            )}
          </ProductImageWrapper>
        );
      })}
    </>
  );
};

const PrimaryProductElement = ({
  primaryImage,
  PrimaryImageContent,
  ctaLabel,
  dataTestId,
  onPrimaryCtaClick,
  ctaVariant = 'secondary',
}: Omit<
  OutfitCollageProps,
  'onClick' | 'data-testid' | 'className' | 'secondaryImages'
> & {dataTestId?: string}) => {
  return (
    <PrimaryProduct
      data-show-full-image={!primaryImage.padded}
      onClick={onPrimaryCtaClick}
    >
      <PrimaryProductImage {...primaryImage} breakpoints={[{columns: 2}]} />
      {PrimaryImageContent}
      <CTA
        data-testid={`${dataTestId}Cta`}
        variant={ctaVariant}
        aria-label={undefined}
      >
        {ctaLabel}
      </CTA>
    </PrimaryProduct>
  );
};

export const OutfitCollage: React.FC<OutfitCollageProps> = ({
  'data-testid': dataTestId,
  className,
  ctaLabel,
  onClick,
  primaryImage,
  PrimaryImageContent,
  secondaryImages,
  primaryImageLinkTarget,
  onPrimaryCtaClick,
  PrimaryTracker = React.Fragment,
  renderWrapperAs,
  ctaVariant,
}) => {
  const {t} = useTranslation();
  if (!secondaryImages.length) {
    return null;
  }

  return (
    <Wrapper
      data-testid={dataTestId}
      className={className}
      onClick={onClick}
      as={renderWrapperAs}
    >
      <PrimaryTracker>
        {primaryImageLinkTarget ? (
          <StyledUnstyledLink
            to={primaryImageLinkTarget.url}
            openExternalLinkInNewTab={primaryImageLinkTarget.external}
            aria-label={t('Go to the outfit page')}
          >
            <PrimaryProductElement
              ctaLabel={ctaLabel}
              primaryImage={primaryImage}
              PrimaryImageContent={PrimaryImageContent}
              dataTestId={dataTestId}
              ctaVariant={ctaVariant}
            />
          </StyledUnstyledLink>
        ) : (
          <PrimaryProductElement
            ctaLabel={ctaLabel}
            primaryImage={primaryImage}
            PrimaryImageContent={PrimaryImageContent}
            dataTestId={dataTestId}
            onPrimaryCtaClick={onPrimaryCtaClick}
            ctaVariant={ctaVariant}
          />
        )}
      </PrimaryTracker>
      <SecondaryProducts>
        <SecondaryProductElement secondaryImages={secondaryImages} />
      </SecondaryProducts>
    </Wrapper>
  );
};

const StyledUnstyledLink = styled(UnstyledLink)`
  display: flex;
  flex: 2;
`;

const CTA = styled(Button)`
  padding: 14px;

  ${desktopAndUpLinaria(`
    &&& {
      max-height: unset;
      opacity: 0;
      transform: translateY(60%);
      transition: opacity 0.4s, transform 0.4s, background-color 300ms ease-in-out, box-shadow 300ms ease-in-out;
    }
  `)}
`;

const Wrapper = styled.div`
  display: flex;
  grid-gap: 2px;
  width: 100%;
  border-radius: 6px;

  @media ${mediaDesktopAndUp} {
    padding: 6px;
    grid-gap: 3px;

    &:hover {
      outline: 1px solid ${COLORS.Grey3};
      outline-offset: -1px;

      ${CTA} {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const PrimaryProductImage = styled(ProductImage)`
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const PrimaryProduct = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex: 2;

  padding: 22px 15px;
  background-color: ${COLORS.ProductImageBackground};

  &[data-show-full-image='true'] {
    position: relative;
    align-items: center;
    justify-content: flex-end;
    padding: unset;
    background-color: unset;

    ${PrimaryProductImage} {
      position: absolute;
      height: 100%;
    }

    ${CTA} {
      width: calc(100% - 30px);
      margin-bottom: 22px;
    }
  }
`;

const SecondaryProducts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;
`;

const ProductImageWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  flex: 1 1 0%;
  padding: 8px 13px;
  background-color: ${COLORS.ProductImageBackground};
`;
